<template>
  <m-content
    class="update-template"
  >
    <editable-page-header
      :title="title"
      :icon="icon"
      :placeholder="$t('list.noTitle')"
      :show-emoji="icon !== '' || emojiAdded"
      show-icon-on-top
      class="_header"
      :disabled="!canEdit"
      @change-title="changeTitle"
      @change-icon="changeIcon"
    >
      <template #actions>
        <m-btn
          v-if="icon === '' && !emojiAdded && canEdit"
          hide-border
          class="_item"
          icon="smile"
          light
          small
          @click="addEmoji"
        >
          {{ $t('page.addEmoji') }}
        </m-btn>
      </template>
    </editable-page-header>
    <div class="_properties">
      <property-values
        :properties="updateProperties"
        :value="propertyValues"
        :disabled="!canEdit"
        @input="updateProps"
      />
    </div>
    <div class="_template">
      <m-editor
        :initial-value="template"
        :placeholder="$t('updateTemplate.placeholder')"
        :allowed-content="allowedContent"
        :disabled="!canEdit"
        :default-font-size="$fontSizes[5]"
        :ctrl-enter-handlers="[onCmdOrModEnter]"
        :mod-enter-handlers="[onCmdOrModEnter]"
        @input="changeTemplate"
      />
    </div>
  </m-content>
</template>

<script>
import EditablePageHeader from '@/components/page/EditablePageHeader.vue';
import MEditor from '@/components/editor/MEditor.vue';
import PropertyValues from '@/components/PropertyValues.vue';
import useAccess from '@/composables/access/access';
import useProperties from '@/composables/property/property';
import { editorNodeType, featureFlag } from 'shared/constants.json';

export default {
  name: 'UpdateTemplate',
  props: {
    updateTemplate: {
      type: Object,
      default: () => null,
    },
    onUpdate: {
      type: Function,
      required: true,
    },
    canEdit: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const allowedContent = [
      editorNodeType.codeBlock,
      editorNodeType.blockquote,
      editorNodeType.bulletList,
      editorNodeType.orderedList,
    ];

    const { accountHasFeature } = useAccess();
    if (accountHasFeature([featureFlag.fileUpload])) {
      allowedContent.push(editorNodeType.image, editorNodeType.file);
    }

    const { updateProperties } = useProperties();
    return { updateProperties, allowedContent };
  },
  components: { MEditor, EditablePageHeader, PropertyValues },
  emits: ['submit'],
  data() {
    return {
      template: this.updateTemplate.template,
      title: this.updateTemplate.title,
      icon: this.updateTemplate.icon,
      propertyValues: this.updateTemplate.propertyValues,
      emojiAdded: false,
    };
  },
  methods: {
    addEmoji() {
      this.emojiAdded = true;
    },
    updateProps(value) {
      this.propertyValues = value;
      this.onUpdate({
        icon: this.icon,
        template: this.template,
        title: this.title,
        propertyValues: this.propertyValues,
      });
    },
    changeTitle(value) {
      this.title = value;
      this.onUpdate({
        icon: this.icon,
        template: this.template,
        title: this.title,
        propertyValues: this.propertyValues,
      });
    },
    changeIcon(value) {
      if (value === '') {
        this.emojiAdded = false;
      }
      this.icon = value;
      this.onUpdate({
        icon: this.icon,
        template: this.template,
        title: this.title,
        propertyValues: this.propertyValues,
      });
    },
    changeTemplate(value) {
      this.template = value;
      this.onUpdate({
        icon: this.icon,
        template: this.template,
        title: this.title,
        propertyValues: this.propertyValues,
      });
    },
    onCmdOrModEnter() {
      this.$emit('submit');
      return true;
    },
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .update-template {
    ._properties {
      margin-top: 2rem;
    }

    ._template {
      margin-top: 2rem;
      margin-left: .6rem;
    }
  }
</style>
